import { useEffect, useMemo, useState } from 'react';
import { Link, Redirect, useParams } from 'react-router-dom';
import { Button, Grid, withTheme } from '@material-ui/core';
import moment from 'moment-timezone';

import { Alert } from '@material-ui/lab';
import BookingProgressBar from '../../components/fancy/BookingProgressBar';
import BookingSummaryContainer from '../../containers/BookingSummaryContainer';
import GuestExpansionPanel from '../../components/GuestExpansionPanel';
import BookerFieldsContainer from '../../containers/BookerFieldsContainer';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import CustomBookingContainer from './components/CustomBookingContainer';
import AgentNoteSection from '../../components/AgentNoteSection';
import BookingConfigurationItem from '../../components/BookingConfigurationItem';
import CartItem from '../cart/components/CartItem';
import VendorInfo from './components/VendorInfo';
import GuestInfo from './components/GuestInfo';
import { useNotify } from '../../hooks/useNotify';
import { useSwaggerClient } from '../../hooks/useSwaggerClient';
import BookingWindowWarning from './components/BookingWindowWarning';
import DiscountEditor from './components/DiscountEditor';

import {
    getBookerFieldsFromCartitems,
    checkBookerFieldsAreOk,
    checkCustomBookingFieldsAreOk,
    checkGuestFieldsAreOk
} from '../../guestfields';
import { withGuestStayDates } from '../../utils';
import { isEditorValueEmpty } from '../../components/drafteditor';
import { useGA } from '../../hooks/useGoogleAnalytics';
import GuestActions from './components/GuestActions';
import TicketList from '../../components/ticket-picker/TicketList';
import FireKnifeSeatsInfo from './components/FireKnifeSeatsInfo/FireKnifeSeatsInfo';

function ReviewPage({
    cartItems,
    booker,
    theme,
    updateItemGuest,
    customer,
    user,
    createGuest,
    acceptBookingNote,
    setAgentNote,
    setHasCustomBooking,
    addAddon,
    updateCartItem
}) {
    const { showModal } = useModals();
    const { cartitemid } = useParams();
    const { notifyError } = useNotify();
    const [ticketCounts, setTicketCounts] = useState([]);
    const [discountAmountError, setDiscountAmountError] = useState(false);

    const { track } = useGA();

    // these hacky values replace the map
    const cartitemIndex = cartitemid === 'last' ? cartItems.length - 1 : parseInt(cartitemid, 10);

    const isAgent = user && user.role === 'agent';
    const isAdmin = user && user.role === 'admin';
    const isAgentOrAdmin = isAgent || isAdmin;

    const defaultHotelId = useMemo(() => {
        const hotelField = customer?.guest_field_info?.find(field => field.hotel);
        return hotelField?.hotel?.id || null;
    }, [customer?.guest_field_info]);

    // these hacky values replace the map
    const cartitem = cartItems[cartitemIndex];
    const [activity, loading, error] = useSwaggerClient('activities', 'getActivityById', {
        activityId: cartitem?.activity_id
    });

    const FireKnifeApiData = Object.keys(cartitem?.providerData || {}).find(
        key => key === 'FireKnifeApi'
    );

    // Fetch bookings for the cart item to check for overlaps
    const isGuestUser = user?.id === customer?.id;
    const userId = isGuestUser ? 'me' : customer?.id;
    const startOfDay = cartitem?.start_time
        ? moment(cartitem.start_time)
              .startOf('day')
              .toLocaleString()
        : '';
    const endOfDay = cartitem?.end_time
        ? moment(cartitem.end_time)
              .endOf('day')
              .toLocaleString()
        : '';

    const [bookingsData] = useSwaggerClient(
        'users',
        'getBookingsByUserId',
        {
            userId,
            future: true,
            page: 0,
            pageSize: 100,
            startDate: startOfDay,
            endDate: endOfDay,
            filters: JSON.stringify({ status: 'all' }),
            calendar: true
        },
        [],
        data =>
            data.bookingItems.filter(booking =>
                ['confirmed', 'unconfirmed'].includes(booking.status)
            ),
        // Skip fetching if no date range or no user
        !startOfDay || !endOfDay || !userId
    );

    useEffect(() => {
        if (
            typeof cartitem !== 'undefined' &&
            typeof cartitem.activity !== 'undefined' &&
            !isEditorValueEmpty(cartitem.activity.booking_notes) &&
            cartitem.acceptedBookingNotes !== true
        ) {
            showModal(MODAL_TYPE.ACCEPT_BOOKING_NOTE, {
                title: 'By booking this activity you accept the below mandatory requirements.',
                content: cartitem.activity.booking_notes,
                buttonLabel: 'Accept',
                onSubmit: () => acceptBookingNote(cartitemIndex)
            });
        }
    }, [acceptBookingNote, cartitem, cartitemIndex, showModal]);

    useEffect(() => {
        setTicketCounts(
            cartitem?.activity.tickets.map(e => {
                return {
                    id: e.id,
                    value: cartitem.tickets.find(t => e.id === t.id)
                        ? cartitem.tickets.find(t => e.id === t.id).quantity
                        : 0
                };
            })
        );
    }, [setTicketCounts, cartitem?.activity.tickets, cartitem?.tickets]);

    // Initialize hotel fields with default value for all guests
    // Only sets the value if the field has never been set (key doesn't exist in guest object)
    useEffect(() => {
        if (!defaultHotelId || !cartitem?.tickets) {
            return;
        }

        let hasUpdates = false;
        const updatedTickets = cartitem.tickets.map(ticket => {
            const hotelFields = ticket.booking_fields?.filter(f => f.type === 'hotel') || [];
            if (hotelFields.length === 0) {
                return ticket;
            }

            const updatedGuests = ticket.guests.map(guest => {
                const guestUpdates = {};
                hotelFields.forEach(field => {
                    // Only set if the field was never touched (key doesn't exist)
                    if (!Object.hasOwn(guest, field.id)) {
                        guestUpdates[field.id] = defaultHotelId;
                        hasUpdates = true;
                    }
                });
                if (Object.keys(guestUpdates).length > 0) {
                    return { ...guest, ...guestUpdates };
                }
                return guest;
            });

            if (updatedGuests !== ticket.guests) {
                return { ...ticket, guests: updatedGuests };
            }
            return ticket;
        });

        if (hasUpdates) {
            updateCartItem(cartitemIndex, { ...cartitem, tickets: updatedTickets });
        }
    }, [defaultHotelId, cartitem, cartitemIndex, updateCartItem]);

    // nothing in the cart
    if (!cartItems || !cartItems.length || cartItems.length < cartitemIndex) {
        return <Redirect to="/" />;
    }

    const updateTickets = (ticketId, ticketQuantity) => {
        const ticket = cartitem.activity.tickets.find(t => t.id === ticketId);
        if (!ticket) {
            return;
        }

        const oldQuantity = ticketCounts.find(t => t.id === ticketId)?.value || 0;
        const quantityDelta = Math.abs(ticketQuantity - oldQuantity);
        const itemToTrack = {
            item_id: `${cartitem.activity.id}-${ticket.id}`,
            item_name: `${cartitem.activity.name} | ${ticket.name}`,
            price: ticket.ticket_retail_price || ticket.retail_price || 0,
            quantity: quantityDelta
        };
        const trackValue = itemToTrack.price * quantityDelta;

        if (ticketQuantity > oldQuantity) {
            track.addToCart({
                items: [itemToTrack],
                currency: 'USD',
                value: trackValue
            });
        } else {
            track.removeFromCart({
                items: [itemToTrack],
                currency: 'USD',
                value: trackValue
            });
        }

        const allTicketQuanatityBesidesThisone = cartitem.tickets
            .filter(e => e.id !== ticketId)
            .reduce((sum, t) => sum + t.quantity, 0);

        if (allTicketQuanatityBesidesThisone === 0 && ticketQuantity === 0) {
            // cannot remove last ticket warning
            notifyError('Cannot remove the last ticket! Must have at least 1 ticket to continue.');
            // we also need to roll back the "other" ticket list
            setTicketCounts(
                ticketCounts.map(e => {
                    if (e.id !== ticketId) {
                        return e;
                    }
                    return { ...e, value: 1 };
                })
            );
            return;
        }

        const quantity = parseInt(ticketQuantity, 10);
        let ticketIndex = cartitem.tickets.findIndex(e => e.id === ticketId);
        if (ticketIndex === -1) {
            cartitem.tickets.push({
                id: ticket.id,
                name: ticket.name,
                booking_fields: ticket.booking_fields,
                quantity,
                retail_price: ticket.retail_price,
                guests: Array(quantity).fill({}),
                providerData: ticket.providerData || {}
            });
            ticketIndex = cartitem.tickets.length - 1;
        }
        if (quantity === 0) {
            cartitem.tickets.splice(ticketIndex, 1);
        } else {
            const oldQuantity = cartitem.tickets[ticketIndex].quantity;
            const difference = oldQuantity - quantity;
            if (difference > 0) {
                // ticket number decrease
                cartitem.tickets[ticketIndex].guests = cartitem.tickets[ticketIndex].guests.slice(
                    0,
                    quantity
                );
            } else if (difference < 0) {
                // ticket number increase, and difference is negative, so we need absolute value
                cartitem.tickets[ticketIndex].guests.splice(
                    oldQuantity,
                    0,
                    ...Array(Math.abs(difference)).fill({})
                );
            }
            cartitem.tickets[ticketIndex].quantity = quantity;
        }
        // A new ticket row / a raised quantity means fresh, empty guest slots —
        // give them the same stay-date prefill an add-to-cart would have.
        updateCartItem(cartitemIndex, withGuestStayDates(cartitem, customer));
    };

    // booker fields needs to be collected for ALL tickets (even if they are not connected to the cartitem)
    const bookerFields = getBookerFieldsFromCartitems(cartItems);
    const bookerRequiredFieldsOk = checkBookerFieldsAreOk(booker, bookerFields, isAgent);

    // checking every cartitem, within that every ticket, with every ticket every guest
    const guestsRequiredFieldsOk = checkGuestFieldsAreOk([cartitem], isAgent);

    // check if agent filled out every custom booking field
    const customBookingRequiredFieldsOk = checkCustomBookingFieldsAreOk([cartitem]);

    // track FK seat confirmation from iframe
    const [seatsConfirmed, setSeatsConfirmed] = useState(!FireKnifeApiData);

    const guestCount = cartitem.tickets.reduce((count, { quantity }) => count + quantity, 0);

    // allow agents to book all Vendors into the past even if they are not the client, we show warning in this case
    const hawaiiNow = moment()
        .tz('Pacific/Honolulu')
        .format('YYYY-MM-DD HH:mm:ss');

    const agentsForDifferentClientInPastWarning =
        isAgent &&
        activity &&
        activity?.different_agent_vendor &&
        !moment.utc(hawaiiNow).isBefore(moment.utc(cartitem?.start_time));

    return (
        <Grid container className="page" spacing={2}>
            <Grid item container md={8} xs={12} direction="column" justifyContent="flex-start">
                <Link
                    to="/cart"
                    className="link mb-2"
                    style={{ color: theme.palette.primary.main }}
                >
                    Back to Cart
                </Link>
                <h1 className="pageheader">Review</h1>
            </Grid>
            <Grid item className="mb-2" md={4} xs={12}>
                <BookingProgressBar activeStep={0} />
            </Grid>
            <Grid item xs={12} md={8}>
                <CartItem
                    user={user}
                    customer={customer}
                    cartItemIndex={cartitemIndex}
                    cartItem={cartitem}
                    isAgent={isAgent}
                    booker={booker}
                    bookingsData={bookingsData}
                    showWarning={false}
                    showImage={false}
                    hasErrors={false}
                >
                    {agentsForDifferentClientInPastWarning && (
                        <Grid item>
                            <Alert severity="warning">
                                This activity starts in the past, make sure you want to book it.
                            </Alert>
                        </Grid>
                    )}
                    <BookingWindowWarning
                        island={activity?.island}
                        isAgent={isAgent}
                        min_booking_window={activity?.min_booking_window}
                        start_time={cartitem.start_time}
                    />
                    {isAgent && <VendorInfo activity={activity} loading={loading} error={error} />}
                    {isAgentOrAdmin && <GuestInfo customer={customer} />}
                    {isAgent && !cartitem.hasCustomBooking && (
                        <Grid item>
                            <Button onClick={() => setHasCustomBooking(cartitemIndex, true)}>
                                Start Custom Booking
                            </Button>
                        </Grid>
                    )}
                </CartItem>
                {isAgent && cartitem.hasCustomBooking && (
                    <CustomBookingContainer
                        cartitem={cartitem}
                        cartitemIndex={cartitemIndex}
                        customerId={customer?.id}
                    />
                )}
                <Grid container spacing={2}>
                    {isAgent && (
                        <AgentNoteSection
                            cartitem={cartitem}
                            cartitemIndex={cartitemIndex}
                            setAgentNote={setAgentNote}
                        />
                    )}
                    <BookingConfigurationItem
                        activityId={cartitem.activity_id}
                        activity_start={cartitem.start_time}
                        guestCount={guestCount}
                        itemAddons={cartitem.addons}
                        addAddon={addon => addAddon(cartitemIndex, addon)}
                    />
                    <Grid item xs={12}>
                        <h2>Information</h2>
                        <p>
                            The activity that you have selected requires some additional
                            information. If areas in this section are left blank, you may be
                            required to provide the information at the time of check-in.
                        </p>
                    </Grid>
                    {cartitem.tickets
                        .filter(ticket => ticket.quantity > 0)
                        .map((ticket, ticketIndex) =>
                            ticket.guests.map((guest, guestIndex) => (
                                <GuestExpansionPanel
                                    isAgent={isAgent}
                                    key={`${cartitemIndex}-${ticketIndex}-${guestIndex}`}
                                    guest={guest}
                                    guestLabel={`Guest information: ${ticket.name} #${guestIndex +
                                        1}`}
                                    fields={ticket.booking_fields.filter(
                                        ({ bookerOnly }) =>
                                            bookerOnly === 'guest' ||
                                            (guestIndex === 0 && bookerOnly === 'item')
                                    )}
                                    onGuestChange={newGuest =>
                                        updateItemGuest(
                                            cartitemIndex,
                                            ticket.id,
                                            guestIndex,
                                            newGuest
                                        )
                                    }
                                    defaultHotelId={defaultHotelId}
                                    // FareharborDirect lodging props
                                    activityId={cartitem.activity_id}
                                    availabilityPk={cartitem.availabilityPk}
                                />
                            ))
                        )}
                    <BookerFieldsContainer defaultHotelId={defaultHotelId} />
                    {FireKnifeApiData && (
                        <FireKnifeSeatsInfo
                            cartItem={cartitem}
                            updateCartItem={(updatedData, confirmed) => {
                                setSeatsConfirmed(!!confirmed);
                                updateCartItem(cartitemIndex, {
                                    ...cartitem,
                                    providerData: {
                                        ...cartitem.providerData,
                                        [FireKnifeApiData]: {
                                            ...cartitem.providerData[FireKnifeApiData],
                                            seats: updatedData
                                        }
                                    }
                                });
                            }}
                        />
                    )}
                </Grid>
            </Grid>
            <Grid item md={4} xs={12}>
                <Grid item xs={12} className="mb-2">
                    <BookingSummaryContainer onlyCartItem={cartitemIndex}>
                        <Grid item xs={12}>
                            {bookerRequiredFieldsOk &&
                            guestsRequiredFieldsOk &&
                            seatsConfirmed &&
                            customBookingRequiredFieldsOk ? (
                                false ? (
                                    <Button
                                        fullWidth
                                        className="btn-squared"
                                        variant="contained"
                                        color="primary"
                                        disabled
                                    >
                                        Please, select the discounts correctly
                                    </Button>
                                ) : (
                                    <Button
                                        fullWidth
                                        className="btn-squared"
                                        style={{ borderRadius: '0px' }}
                                        variant="contained"
                                        color="primary"
                                        component={Link}
                                        to="/cart"
                                        disabled={cartitem.tickets.length === 0}
                                    >
                                        Proceed to Cart
                                    </Button>
                                )
                            ) : (
                                <Button
                                    fullWidth
                                    className="btn-squared"
                                    variant="contained"
                                    color="primary"
                                    disabled
                                >
                                    Please, complete guest information
                                </Button>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            {isAgentOrAdmin &&
                                !customer &&
                                !discountAmountError &&
                                bookerRequiredFieldsOk &&
                                guestsRequiredFieldsOk &&
                                seatsConfirmed &&
                                customBookingRequiredFieldsOk && (
                                    <GuestActions createGuest={createGuest} />
                                )}
                        </Grid>
                    </BookingSummaryContainer>
                </Grid>
                {isAgent && (
                    <Grid
                        container
                        item
                        xs={12}
                        style={{
                            padding: '30px',
                            marginTop: '25px',
                            backgroundColor: '#fafafa',
                            position: 'relative',
                            transition: 'opacity 0.3s linear 0.3s',
                            justifyContent: 'center'
                        }}
                    >
                        <Grid item>
                            <h2>Discount</h2>
                        </Grid>
                        <DiscountEditor
                            cartItem={cartitem}
                            // TODO: later it will be sent by backend
                            // we now store the 'ticket_retail_price' in redux, but when we add ticket from ReviewPage the 'retail_price' is equal to the correct 'ticket_retail_price'
                            // so if we get ticket_retail_price we use it, but if we don't, we prefer retail_price
                            maxValue={cartitem.tickets.reduce(
                                (price, { quantity, ticket_retail_price, retail_price }) =>
                                    price + quantity * (ticket_retail_price || retail_price),
                                0
                            )}
                            updateCartItem={discount =>
                                updateCartItem(cartitemIndex, { ...cartitem, discount })
                            }
                            showAgencyDiscount={window.clientData.custom_agency_discount}
                            showVendorDiscount={window.clientData.custom_vendor_discount}
                            discountAmountError={discountAmountError}
                            setDiscountAmountError={setDiscountAmountError}
                            role={user?.role}
                        />
                    </Grid>
                )}
                <Grid
                    container
                    item
                    xs={12}
                    style={{
                        padding: '30px',
                        marginTop: '25px',
                        backgroundColor: '#fafafa',
                        position: 'relative',
                        transition: 'opacity 0.3s linear 0.3s',
                        justifyContent: 'center'
                    }}
                >
                    <Grid item>
                        <h2>Tickets</h2>
                    </Grid>
                    <Grid item xs={12} container>
                        <TicketList
                            tickets={cartitem.activity.tickets}
                            startTime={cartitem.start_time}
                            role={user?.role}
                            ticketCounts={ticketCounts}
                            setTicketCounts={setTicketCounts}
                            updateTickets={updateTickets}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    );
}

export default withTheme(ReviewPage);
